import { featureShowcase } from "../data";
import {
  CheckList,
  Heading,
  Pill,
  resolveAssetUrl,
  Section,
  SectionHeader,
  tintTextClass,
} from "../shared";

const glowTintClass = {
  blue: "bg-cerulean/15",
  coral: "bg-coral/15",
  cobalt: "bg-cobalt/20",
  teal: "bg-teal/20",
};

/**
 * Alternating-row feature tour. Each row pairs a large screenshot with a
 * headline, body copy, and three checkmark value props.
 *
 * @returns {import("react").ReactElement}
 */
export function FeatureShowcase() {
  return (
    <Section id="product-showcase" className="bg-crystal/40">
      <SectionHeader
        tag="Feature tour"
        title="Everything your growth team needs"
        subtitle="Four surfaces. One partnership loop. Designed for marketers who need to move fast."
      />
      <div className="flex flex-col gap-16 md:gap-24">
        {featureShowcase.map((feature, idx) => (
          <FeatureRow key={feature.key} feature={feature} flip={idx % 2 === 1} />
        ))}
      </div>
    </Section>
  );
}

/**
 * Single row inside FeatureShowcase. Flips the image/copy ordering at md+ when
 * `flip` is true.
 *
 * @param {Object} props
 * @param {import("../data/featureShowcase").FeatureShowcaseItem} props.feature - row content
 * @param {boolean} props.flip - whether to reverse the copy/image order on desktop
 * @returns {import("react").ReactElement}
 */
function FeatureRow(props) {
  const { feature, flip } = props;

  const copyOrder = flip ? "md:order-2" : "md:order-1";
  const imageOrder = flip ? "md:order-1" : "md:order-2";

  return (
    <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-14">
      <div className={`flex flex-col items-start ${copyOrder}`}>
        <Pill variant="eyebrow" className="mb-6">
          {feature.eyebrow}
        </Pill>
        <Heading as="h3" size="row" className="text-ink mb-5">
          {feature.title}
        </Heading>
        <p className="body-lg text-muted mb-8 max-w-xl">{feature.body}</p>
        <CheckList items={feature.bullets} iconClassName={tintTextClass[feature.tint]} />
      </div>

      <div className={`relative ${imageOrder}`}>
        <span
          aria-hidden="true"
          className={`absolute -inset-6 rounded-4xl blur-3xl ${glowTintClass[feature.tint]} opacity-70`}
        />
        <div className="shadow-card-hover border-line relative overflow-hidden rounded-[20px] border bg-white">
          <img
            src={resolveAssetUrl(feature.imgSrc)}
            alt={feature.alt}
            loading="lazy"
            decoding="async"
            className="block h-auto w-full"
          />
        </div>
      </div>
    </div>
  );
}
